import React, { useCallback, useContext, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { getTimeRangeWithDates, isEndTimeBeforeStartTime } from '@ubeya/shared/utils/time';
import { API_DATE_TIME_FORMAT, MAX_HOURS_SHOW_PROMPT } from '@ubeya/shared/constants';
import { Tooltip } from '@ubeya/shared-web/components';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { isCellClearable, isCellEditable, isReadOnly } from 'shared/utils/timesheet';
import BaseTimeRenderer from './BaseTimeRenderer';

const OriginalTimesheetHours = styled.div`
  color: ${({ theme }) => theme.colors.redNegative};
`;
const ScheduledTimesheetHours = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey3};
`;

const OutTimeRenderer = ({
  rowData,
  columnData: {
    updateEndTime,
    updateOSEndTime,
    timeFormat,
    setIsPromptOpen,
    readOnly: inputReadOnly = false,
    allowUpdateExternal = false,
    showTimesheetScheduledHours = false,
    showTimesheetOriginalHours = false,
    showClientTimesheetOriginalHours = false,
    onOutsideClickSave = false,
    disableWarning = false
  } = {}
}) => {
  const { t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();

  const myPrivileges = useContext(PrivilegesContext);

  const onChangeEndTime = useCallback(
    ({ endTime }) =>
      !rowData.isExternal
        ? updateEndTime({ newTime: endTime, ...rowData })
        : updateOSEndTime({ newTime: endTime, ...rowData }),
    [rowData, updateEndTime, updateOSEndTime]
  );

  const isBreaktimeIncludedInHoursRange = useMemo(
    () =>
      (rowData?.breaktimes || []).some(({ endTime: breakEndTime }) =>
        moment(breakEndTime, API_DATE_TIME_FORMAT).isAfter(moment(rowData?.actualOut, API_DATE_TIME_FORMAT))
      )
        ? t('breakEndsAfterThisHour')
        : null,
    [rowData.actualOut, rowData?.breaktimes, t]
  );

  const handleUpdateTimeSheet = useCallback(
    (newTime, originalNewTime) => {
      const { endTime } = getTimeRangeWithDates({
        date: rowData.date,
        startTime: rowData.actualIn,
        endTime: newTime,
        checkBaseStartDate: true,
        includeEqualStartEndTIme: false
      });

      if (newTime) {
        const duration =
          rowData.actualIn && moment.duration(moment(endTime).diff(rowData.actualIn, 'milliseconds')).asHours();
        if (duration && duration > MAX_HOURS_SHOW_PROMPT) {
          return setIsPromptOpen?.({
            onConfirm: () => onChangeEndTime({ endTime }),
            duration
          });
        }
        if (!moment(endTime, API_DATE_TIME_FORMAT).isSame(moment(rowData.actualOut, API_DATE_TIME_FORMAT))) {
          onChangeEndTime({ endTime });
        }
      } else if (originalNewTime === null) {
        onChangeEndTime({ endTime });
      }
    },
    [rowData.date, rowData.actualIn, rowData.actualOut, setIsPromptOpen, onChangeEndTime]
  );

  const extraDay =
    rowData.actualIn &&
    rowData.actualOut &&
    isEndTimeBeforeStartTime(moment(rowData.actualIn), moment(rowData.actualOut), false);

  const readOnly = inputReadOnly || !rowData.actualIn;

  const isEditable = isCellEditable({
    readOnly: isReadOnly(rowData, readOnly),
    updateFunc: rowData?.isExternal ? updateOSEndTime : updateEndTime,
    isExternal: rowData?.isExternal,
    allowUpdateExternal
  });

  const isClearable = isCellClearable({
    isClientApproved: rowData?.clientApproved && rowData?.isExternal,
    isApproved: rowData?.approved,
    isUnlockPayrollPrivilege: myPrivileges[privileges.UNLOCK_PAYROLL],
    allowClear: true
  });

  const showTimesheetHoursUnderTime = useMemo(() => {
    if (!showTimesheetOriginalHours || !rowData.actualOut || !rowData.timesheetEndTime) {
      return false;
    }

    const actualOutMoment = moment(rowData.actualOut, 'YYYY-MM-DD HH:mm:ss');
    const timesheetEndTimeMoment = moment(rowData.timesheetEndTime, 'YYYY-MM-DD HH:mm:ss');

    actualOutMoment.startOf('minute');
    timesheetEndTimeMoment.startOf('minute');

    return !actualOutMoment.isSame(timesheetEndTimeMoment);
  }, [rowData.actualOut, rowData.timesheetEndTime, showTimesheetOriginalHours]);

  const showClientTimesheetHoursUnderTime = useMemo(() => {
    if (!showClientTimesheetOriginalHours || !rowData.actualOut || !rowData.clientActualOut) {
      return false;
    }

    const actualOutMoment = moment(rowData.actualOut, 'YYYY-MM-DD HH:mm:ss');
    const clientActualOutMoment = moment(rowData.clientActualOut, 'YYYY-MM-DD HH:mm:ss');

    actualOutMoment.startOf('minute');
    clientActualOutMoment.startOf('minute');

    return !actualOutMoment.isSame(clientActualOutMoment);
  }, [rowData.actualOut, rowData.clientActualOut, showClientTimesheetOriginalHours]);

  const showTimesheetScheduledHoursUnderTime = showTimesheetScheduledHours && rowData.scheduleOut;

  const tooltipBody = useMemo(() => {
    if (!rowData.actualOut) return undefined;
    if (rowData.isSystem && rowData.reportMethods?.out?.reportMethod !== 'system') {
      return (
        <>
          <div>{formattingMap.dateAndTime(rowData.actualOut)}</div>
          <div>{t('autoClockedOutAfterCheckOut')}</div>
        </>
      );
    }
    return formattingMap.dateAndTime(rowData.actualOut);
  }, [rowData.actualOut, rowData.isSystem, rowData.reportMethods, formattingMap, t]);

  return (
    <>
      <BaseTimeRenderer
        rowData={rowData}
        showWarning={rowData.actualOut && isBreaktimeIncludedInHoursRange}
        disableWarning={disableWarning}
        actualValue={rowData.actualOut}
        actualUserValue={rowData.userActualOut}
        actualReporter={rowData.actualOutReporter}
        reportMethod={rowData.reportMethods?.out}
        reportedValueMethod={rowData.reportMethods?.reportedOut}
        tooltipBody={tooltipBody}
        scheduleValue={rowData.scheduleOut}
        extraDay={extraDay}
        onChangeActualValue={handleUpdateTimeSheet}
        startTime={rowData.actualIn}
        isEditable={isEditable}
        onOutsideClickSave={onOutsideClickSave}
        clearable={isClearable}
        visible
      />
      {showTimesheetHoursUnderTime && (
        <Tooltip body={t('originalTimesheetEndTime')}>
          <OriginalTimesheetHours>
            {moment(rowData.timesheetEndTime).format(timeFormat || 'hh:mm')}
          </OriginalTimesheetHours>
        </Tooltip>
      )}
      {showTimesheetScheduledHoursUnderTime && (
        <Tooltip body={t('scheduledTimesheetEndTime')}>
          <ScheduledTimesheetHours>{moment(rowData.scheduleOut).format(timeFormat || 'hh:mm')}</ScheduledTimesheetHours>
        </Tooltip>
      )}

      {showClientTimesheetHoursUnderTime && (
        <Tooltip body={t('originalClientTimesheetEndTime')}>
          <OriginalTimesheetHours>
            {moment(rowData.clientActualOut).format(timeFormat || 'hh:mm')}
          </OriginalTimesheetHours>
        </Tooltip>
      )}
    </>
  );
};

export default OutTimeRenderer;
